import type { InspectionEvent, Observer } from "xstate";

/**
 * Actor inspection is deliberately opt-in. The production CDN bundle never
 * installs an observer, while the local development entry point can install a
 * redacting Stately inspector.
 */
let actorInspectionObserver: Observer<InspectionEvent> | undefined;

export function configureActorInspection(
  observer: Observer<InspectionEvent> | undefined,
): void {
  actorInspectionObserver = observer;
}

export function getActorInspectionObserver():
  | Observer<InspectionEvent>
  | undefined {
  return actorInspectionObserver;
}

const SENSITIVE_KEY =
  /(?:address|answer|authorization|buyer|card|customer|email|family|form|guest|member|name|password|payment|phone|recipient|secret|token|waiver)/i;

const REDACTED = "[redacted]";
const MAX_REDACTION_DEPTH = 8;

/**
 * Produces a JSON-safe projection for inspection. Checkout machines must keep
 * vendor/DOM objects out of context, but this remains defence in depth for
 * identity, form, and payment data carried by legitimate domain events.
 */
export function redactInspectionValue(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (depth >= MAX_REDACTION_DEPTH) return "[truncated]";
  if (
    value === null ||
    typeof value === "boolean" ||
    typeof value === "number"
  ) {
    return value;
  }
  if (typeof value === "string") return value;
  if (typeof value === "undefined") return undefined;
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "symbol" || typeof value === "function") {
    return `[${typeof value}]`;
  }

  if (seen.has(value)) return "[circular]";
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => redactInspectionValue(item, depth + 1, seen));
  }

  if (value instanceof Date) return value.toISOString();
  if (value instanceof Set) {
    return [...value].map((item) =>
      redactInspectionValue(item, depth + 1, seen),
    );
  }
  if (value instanceof Map) {
    return Object.fromEntries(
      [...value.entries()].map(([key, item]) => [
        String(key),
        redactInspectionValue(item, depth + 1, seen),
      ]),
    );
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEY.test(key)
        ? REDACTED
        : redactInspectionValue(item, depth + 1, seen),
    ]),
  );
}

export function redactInspectionEvent<T extends { type: string }>(event: T): T {
  return redactInspectionValue(event) as T;
}

export function redactInspectionContext(
  context: Record<string, unknown>,
): Record<string, unknown> {
  return redactInspectionValue(context) as Record<string, unknown>;
}
